/** @jsxImportSource @emotion/react */
import PropTypes from 'prop-types';
import {
  forwardRef,
  Fragment,
  useEffect,
  useImperativeHandle,
  useReducer,
  useRef,
} from 'react';

import { AGE_ID, EMPTY_STRING } from '../../const';
import { useOnboardingDataContext } from '../../context/OnboardingData';
import ageGroupReducer from '../../reducers/ageGroup';
import getCategoryDataLayer from '../../utils/getCategoryDataLayer';
import getNextQuestionId from '../../utils/getNextQuestionId';
import idFromString from '../../utils/idFromString';
import useStyles from './styles';

const AgeGroupQuestion = forwardRef(({
  readableId,
  answers,
  ageRanges,
  onAnswer,
  selectedAge,
  headerTitle,
  nextClickAnswer,
}, ref) => {
  // Ref for the main scrollable container
      const containerRef = useRef(null);
  const styles = useStyles();
  const [answersState, dispatch] = useReducer(ageGroupReducer, []);
  const selectedAnswer = JSON.parse(sessionStorage.getItem('ISVOLUNTEER'))
    ? answers?.map(item => item?.options)
    : answers.filter(item => item.age === parseInt(selectedAge, 10))[0].options;
  const { arrowContext, OnboardingContext, setArrowFunction, setOnboardingDataContext } =
    useOnboardingDataContext();

  const changeData = ageData => {
    switch (ageData) {
      case 'U6-U7':
        return ['U6,U7'];
      case 'U8-U10':
        return ['U8,U9,U10'];
      case 'U11-U12':
        return ['U11,U12'];
      case 'U13-U14':
        return ['U13,U14'];
      case 'U15-U16':
        return ['U15,U16'];
      case 'U17-U18':
        return ['U17,U18'];
      case 'Open Aged':
        return ['Open Aged'];
      default:
        return 'Open Aged';
    }
  };

  const onChange = event => {
    window.scrollTo(0, 0);
    setArrowFunction(true);
    const ageData = event.target.value;
    const id = JSON.parse(sessionStorage.getItem('ISVOLUNTEER'))
      ? changeData(ageData)
      : event.target.value;
    if (event.target.checked) {
      dispatch({ type: 'add', payload: { id } });
    } else {
      dispatch({ type: 'remove', payload: { id } });
    }
  };

  // Refined handler to automatically scroll the focused item into view before selection
    const handleFocus = event => {
      // Use the standard browser scrollIntoView method on the input element's container (the label)
      // 'center' is usually best for tabbing navigation
      event.target.parentElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    };

  const ageGroupOption = () => {
    const isVolunteer = JSON.parse(sessionStorage.getItem('ISVOLUNTEER'));
    const selectedAgeGroupIds = answersState.map(a => a.AgeGroupId).toString();

    const getVolunteerAge = () => {
      const matchedAnswer = answers?.filter(
        item => selectedAgeGroupIds === item?.ageOptions.toString()
      )[0];
      return matchedAnswer?.age ?? EMPTY_STRING;
    };

    setTimeout(() => {
      onAnswer({
        answerData: {
          [readableId]: selectedAgeGroupIds,
          [isVolunteer ? AGE_ID : EMPTY_STRING]: isVolunteer
            ? getVolunteerAge()
            : EMPTY_STRING,
        },
        nextQuestionId: getNextQuestionId({ ageRanges, selectedAge }),
      });
    }, 500);
  };    

  useImperativeHandle(ref, () => ({
    handleNextNav: () => {
      ageGroupOption();
    },
  }));

  useEffect(() => {
    setOnboardingDataContext({
      ...OnboardingContext,
      question: headerTitle,
      agegroup: answersState[0]?.AgeGroupId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerTitle, answersState]);

  // --- Scroll effects logic (remains mostly the same, using containerRef) ---
      useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
  
        const timer = setTimeout(() => {
          container.scrollTop += 1;
          container.addEventListener('scroll', handleScroll);
          return () => {
            clearTimeout(timer);
            container.removeEventListener('scroll', handleScroll);
          };
        }, 300);
        return () => clearTimeout(timer);
      }, []);

  const handleScroll = () => {
      const container = containerRef.current;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const blurZoneHeight = container.clientHeight * 0.4;
      const blurZoneTop = containerRect.bottom - blurZoneHeight;
      // Check if the user has scrolled to the bottom (allowing a small margin)
      const bottomscroll =
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - 1;
      const labels = container.getElementsByTagName('label');
      const options = [];
      for (let i = 0; i < labels.length; i++) {
        options.push(labels[i]);
      }
      if (bottomscroll) {
        options.forEach(option => {
          option.classList.remove('fade');
          option.style.opacity = '1';
        });
        return;
      }
      // For each option, apply a progressive fade if it falls into the blur zone
      options.forEach(option => {
        const optionRect = option.getBoundingClientRect();
        // If the top of the option is within the blur zone, calculate fade amount
        if (optionRect.top >= blurZoneTop) {
          // Calculate a progress value (0 at the top of the blur zone, 1 at the very bottom)
          const progress = (optionRect.top - blurZoneTop) / blurZoneHeight;
          // Invert progress so that items closer to bottom get more fade
          const invertedProgress = 1 - progress;
          // Set opacity between 0.5 (minimal) and 1 (maximum)
          const minOpacity = 0.5,
            maxOpacity = 1;
          const opacityValue =
            minOpacity + (maxOpacity - minOpacity) * invertedProgress;
          option.classList.add('fade');
          option.style.opacity = opacityValue;
        } else {
          // Items above the blur zone remain clear
          option.classList.remove('fade');
          option.style.opacity = '1';
        }
      });
    };

  return (
    <Fragment>
      <div 
        ref={containerRef}
        onScroll={handleScroll}
        css={styles.base}
        tabIndex={-1}
      >
        {selectedAnswer &&
          selectedAnswer.map(value => {
            const optionId = JSON.parse(sessionStorage.getItem('ISVOLUNTEER'))
              ? value
              : idFromString(value);
            const isChecked = !!answersState.find(answer =>
              JSON.parse(sessionStorage.getItem('ISVOLUNTEER'))
                ? answer.AgeGroupId.toString().split(',', 1)[0] ===
                  value[0].split('-', 1)[0]
                : answer.AgeGroupId === value
            );

            return (
              <label css={styles.label} key={value}>
                <input
                  tabIndex={0}
                  css={styles.checkbox}
                  type="checkbox"
                  id={optionId}
                  name={optionId}
                  value={value}
                  onClick={onChange}
                  onFocus={handleFocus}
                  role='button'
                  aria-pressed={isChecked}
                />
                <span css={styles.answer(isChecked)}>{value}</span>
              </label>
            );
          })}
        </div>
      <button
        css={styles.buttonNext(arrowContext)}
        onClick={() => {
          nextClickAnswer();
           ageGroupOption();
            setArrowFunction(false);
            getCategoryDataLayer({
              eventAction: `Step ${OnboardingContext.step} - Next CTA`,
              eventLabel: OnboardingContext.question,
              eventparameter1: `${OnboardingContext.age}${
                OnboardingContext.agegroup
                  ? ' , ' + OnboardingContext.agegroup
                  : ''
              }`,
              gender_group: OnboardingContext.gender_group,
              age_youth_group: OnboardingContext.agegroup,
              event_name: 'next_button_click',
            });
          }}
        aria-label="Next" 
        disabled={arrowContext === false ? true : false}
      > Next </button>
    </Fragment>
  );
});
AgeGroupQuestion.propTypes = {
  readableId: PropTypes.number,
  answers: PropTypes.string,
  onAnswer: PropTypes.func,
  selectedAge: PropTypes.number,
  ageRanges: PropTypes.string,
  headerTitle: PropTypes.string,
};
export default AgeGroupQuestion;
